/**
 * Zod Validation Schemas for Tutor Registration
 * 
 * 7-step layout:
 * Step 0: Tuition Type
 * Step 1: Teaching Preferences (Area + Time)
 * Step 2: Subjects by Level
 * Step 3: Personal Profile (Education + Teaching Languages)
 * Step 4: Hourly Rate & Custom Language
 * Step 5: Tutor Experience + Public Exam Results
 * Step 6: Teaching Style + About Me + Terms
 */

import { z } from 'zod';

/**
 * Step 0: Tuition Type
 */
export const tuitionTypeSchema = z.object({
  tuitionType: z.enum(['physical', 'online', 'both'], {
    message: 'Please select at least one tuition type',
  }),
});

/**
 * Step 1: Teaching Preferences (Area + Time merged)
 */
export const teachingPreferencesSchema = z.object({
  tuitionType: z.string(),
  preferredState: z.string().optional(),
  preferredDistricts: z.array(z.string()).optional(),
  preferredAreaNotes: z.array(z.string().max(80, 'Area note must be at most 80 characters')).max(20, 'At most 20 area notes').optional(),
  availability: z
    .record(z.string(), z.array(z.string()))
    .refine(
      (avail) => Object.keys(avail).length > 0 && Object.values(avail).some((times) => times.length > 0),
      { message: 'Please select at least one available time slot' }
    ),
}).superRefine((data, ctx) => {
  if (data.tuitionType !== 'online' && (!data.preferredState || data.preferredState.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please select a state',
      path: ['preferredState'],
    });
  }
  if (data.tuitionType !== 'online' && (!data.preferredDistricts || data.preferredDistricts.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please select at least one district',
      path: ['preferredDistricts'],
    });
  }
});

/**
 * Step 2: Subjects by Education Level (Multi-level support)
 */
export const subjectCardSchema = z.object({
  id: z.string(),
  educationSystem: z.string().min(1, 'Please select an education system'),
  educationLevels: z
    .array(z.string())
    .min(1, 'Please select at least one education level'),
  subjects: z
    .array(z.string())
    .min(1, 'Please select at least one subject'),
});

export const subjectsByLevelSchema = z.object({
  subjectCards: z
    .array(subjectCardSchema)
    .min(1, 'Please add at least one subject card')
    .refine(
      (cards) => cards.every((card) => card.educationSystem && card.educationLevels.length > 0 && card.subjects.length > 0),
      { message: 'All subject cards must have an education system, at least one education level, and at least one subject' }
    ),
});

/**
 * Step 3: Personal Profile (Current Status + Education + Teaching Languages)
 */
export const personalProfileSchema = z.object({
  currentStatus: z
    .string()
    .min(1, 'Please select your current status'),
  highestEducationLevel: z
    .string()
    .min(1, 'Please select your highest education level'),
  otherEducationLevel: z.string().optional(),
  school: z
    .string()
    .min(1, 'Please enter your school/university')
    .max(200, 'School name must be less than 200 characters'),
  teachingInstitution: z
    .string()
    .max(200, 'Teaching institution must be less than 200 characters')
    .optional(),
  major: z
    .string()
    .min(1, 'Please enter your major/field of study')
    .max(100, 'Major must be less than 100 characters'),
  teachingLanguages: z
    .array(z.string())
    .min(1, 'Please select at least one teaching language'),
  customLanguage: z.string().optional(),
}).refine(
  (data) => {
    // If education level is "others", otherEducationLevel must be provided
    if (data.highestEducationLevel === 'others') {
      return data.otherEducationLevel && data.otherEducationLevel.trim().length > 0;
    }
    return true;
  },
  {
    message: 'Please enter your education level',
    path: ['otherEducationLevel'],
  }
).refine(
  (data) => {
    // If teaching languages include "other", customLanguage must be provided
    if (data.teachingLanguages.includes('other')) {
      return data.customLanguage && data.customLanguage.trim().length > 0;
    }
    return true;
  },
  {
    message: 'Please specify the language',
    path: ['customLanguage'],
  }
);

/**
 * Step 4: Hourly Rate & Custom Language
 */
export const hourlyRateSchema = z.object({
  customLanguage: z.string().optional(),
  hourlyRate: z
    .string()
    .refine((val) => !isNaN(Number(val)), {
      message: 'Hourly rate must be a number',
    })
    .refine((val) => Number.isInteger(Number(val)), {
      message: 'Hourly rate must be a whole number',
    })
    .refine(
      (val) => Number(val) >= 10 && Number(val) <= 800,
      { message: 'Hourly rate must be between RM 10 and RM 800' }
    ),
});

/**
 * Step 5: Tutor Experience
 */
export const tutorExperienceSchema = z.object({
  tutorExperienceTypes: z
    .array(z.string())
    .min(1, 'Please select at least one experience type'),
  yearsOfTuitionExperience: z
    .string()
    .or(z.number())
    .optional(),
}).superRefine((data, ctx) => {
  const hasNoExperience = data.tutorExperienceTypes.includes('No experience');

  if (!hasNoExperience) {
    const val = data.yearsOfTuitionExperience;
    if (val === '' || val === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please select years of tuition experience',
        path: ['yearsOfTuitionExperience'],
      });
    }
  }

  if (hasNoExperience && data.tutorExperienceTypes.length > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '"No experience" cannot be combined with other experience types',
      path: ['tutorExperienceTypes'],
    });
  }
});

/**
 * Step 6: Public Exam Results (Optional)
 */
export const examSubjectResultSchema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  grade: z.string().min(1, 'Grade is required'),
  isCompulsory: z.boolean(),
});

export const examResultSchema = z.object({
  id: z.string(),
  examType: z.string().min(1, 'Please select an exam type'),
  year: z
    .number()
    .int('Year must be a whole number')
    .min(1990, 'Year must be 1990 or later')
    .max(new Date().getFullYear(), 'Year cannot be in the future'),
  results: z.array(examSubjectResultSchema).optional(),
});

export const publicExamResultsSchema = z.object({
  examResults: z.array(examResultSchema).optional(),
});

/**
 * Step 7: Teaching Style + About Me + Terms
 */
export const teachingStyleSchema = z.object({
  teachingStyle: z.object({
    iAm: z.array(z.string()).optional(),
    iCan: z.array(z.string()).optional(),
    iKnow: z.array(z.string()).optional(),
    others: z
      .string()
      .max(2000, 'Additional teaching characteristics must be less than 2000 characters')
      .optional(),
  }),
});

export const aboutMeSchema = z.object({
  aboutMe: z
    .string()
    .optional()
    .refine(
      (text) => !text || text.trim().length >= 20,
      { message: 'About Me must be at least 20 characters' }
    )
    .refine(
      (text) => !text || text.trim().length <= 5000,
      { message: 'About Me must be less than 5000 characters' }
    ),
});

export const termsSchema = z.object({
  agreedToTerms: z
    .boolean()
    .refine((val) => val === true, {
      message: 'You must agree to the terms and conditions',
    }),
});

/**
 * Complete tutor registration form schema (all steps combined)
 */
export const tutorRegistrationSchema = tuitionTypeSchema
  .merge(teachingPreferencesSchema)
  .merge(subjectsByLevelSchema)
  .merge(personalProfileSchema)
  .merge(hourlyRateSchema)
  .merge(tutorExperienceSchema)
  .merge(publicExamResultsSchema)
  .merge(teachingStyleSchema)
  .merge(aboutMeSchema)
  .merge(termsSchema);

/**
 * Combined schema for Step 6: Teaching Style + About Me + Terms
 */
export const teachingStyleAndAboutMeSchema = teachingStyleSchema
  .merge(aboutMeSchema)
  .merge(termsSchema);

/**
 * Combined schema for Step 5: Tutor Experience + Public Exam Results
 */
export const tutorExperienceAndExamSchema = tutorExperienceSchema
  .and(publicExamResultsSchema);

/**
 * Array of step schemas for per-step validation (exactly 7 entries for 7 steps)
 */
export const stepSchemas = [
  tuitionTypeSchema,                // Step 0: Tuition Type
  teachingPreferencesSchema,        // Step 1: Teaching Preferences
  subjectsByLevelSchema,            // Step 2: Subjects by Level
  personalProfileSchema,            // Step 3: Personal Profile
  hourlyRateSchema,                 // Step 4: Hourly Rate
  tutorExperienceAndExamSchema,     // Step 5: Tutor Experience + Public Exam Results
  teachingStyleAndAboutMeSchema,    // Step 6: Teaching Style + About Me + Terms
] as const;

/**
 * TypeScript type exports
 */
export type TuitionTypeData = z.infer<typeof tuitionTypeSchema>;
export type TeachingPreferencesData = z.infer<typeof teachingPreferencesSchema>;
export type SubjectsByLevelData = z.infer<typeof subjectsByLevelSchema>;
export type PersonalProfileData = z.infer<typeof personalProfileSchema>;
export type HourlyRateData = z.infer<typeof hourlyRateSchema>;
export type TutorExperienceData = z.infer<typeof tutorExperienceSchema>;
export type PublicExamResultsData = z.infer<typeof publicExamResultsSchema>;
export type TeachingStyleData = z.infer<typeof teachingStyleSchema>;
export type AboutMeData = z.infer<typeof aboutMeSchema>;
export type TermsData = z.infer<typeof termsSchema>;
export type TutorRegistrationData = z.infer<typeof tutorRegistrationSchema>;
